import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Spinner,
  Alert,
} from "react-bootstrap";
import { EnvelopeFill, ArrowLeft } from "react-bootstrap-icons";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import * as emailjs from "@emailjs/browser";
import FromPageIndicator from "../Common/FromPageIndicator";
import CustomAlert from "../CustomAlert/CustomAlert";

const ForgetPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  // Alert states
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    variant: "info",
    title: "",
    message: "",
    icon: null,
    autoClose: false,
    autoCloseDelay: 3000,
    position: "center",
  });

  // EmailJS configuration
  const EMAILJS_SERVICE_ID = "service_o05fsjy";
  const EMAILJS_TEMPLATE_ID = "template_y04wbut";
  const EMAILJS_PUBLIC_KEY = "JAMn4MusiqFIy0S0Y";

  // Initialize EmailJS
  React.useEffect(() => {
    emailjs.init(EMAILJS_PUBLIC_KEY);
  }, []);

  // Function to show alert
  const showAlert = (config) => {
    setAlertConfig({
      show: true,
      ...config,
    });
  };

  // Function to hide alert
  const hideAlert = () => {
    setAlertConfig((prev) => ({ ...prev, show: false }));
  };

  const handlePasswordReset = async () => {
    // Validation
    if (!email.trim()) {
      showAlert({
        variant: "warning",
        title: "Missing Information",
        message: "Please enter your email address.",
        icon: "⚠️",
        autoClose: true,
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showAlert({
        variant: "danger",
        title: "Invalid Email",
        message: "Please enter a valid email address.",
        icon: "❌",
        autoClose: true,
      });
      return;
    }

    setLoading(true);

    try {
      // Fetch user data from your API
      const response = await axios.get(
        "https://api.dremerz.net/api/DZ_8921_Ampicon_Create_Users_UserList"
      );

      const data = response.data;
      const user = data && data.find((u) => u.emailId === email);

      if (!user) {
        showAlert({
          variant: "danger",
          title: "Email Not Found",
          message:
            "This email address is not registered with us. Please check your email or register for a new account.",
          icon: "🚫",
          autoClose: true,
          autoCloseDelay: 4000,
        });
        setLoading(false);
        return;
      }

      // Prepare email template parameters
      const templateParams = {
        to_email: email,
        to_name: user.userName || "User",
        user_name: user.userName || "User",
        user_email: user.emailId,
        user_id: user.id || "Not available",
        existing_password: user.password,
        conference_name: "TN PY AMPICON 2025",
        support_email: "tnpyampicon2025@gmail.com",
        support_phone: "+91 9994432135",
      };

      // Send email using EmailJS
      const emailResponse = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );

      if (emailResponse.status === 200) {
        showAlert({
          variant: "success",
          title: "Email Sent Successfully!",
          message:
            "Your account details and password have been sent to your email address. Please check your inbox.",
          icon: "✅",
          autoClose: true,
          autoCloseDelay: 5000,
        });

        setEmail("");

        // Redirect to login page after successful email send
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        throw new Error("Failed to send email");
      }
    } catch (error) {
      console.error("Error processing password reset:", error);

      if (error.response && error.response.status === 429) {
        showAlert({
          variant: "warning",
          title: "Too Many Requests",
          message: "Please wait a moment before trying again.",
          icon: "⏳",
          autoClose: true,
          autoCloseDelay: 4000,
        });
      } else {
        showAlert({
          variant: "danger",
          title: "Coming Soon",
          message: "Connection Work Going On",
          icon: "❌",
          autoClose: true,
          autoCloseDelay: 4000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !loading) {
      handlePasswordReset();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handlePasswordReset();
  };

  return (
    <div style={{ backgroundColor: "#FFF4FA", minHeight: "100vh" }}>
      <style>
        {`
          .form-control:focus {
            border-color: rgba(37, 130, 161, 0.4);
            box-shadow: 0 0 0 0.25rem rgba(37, 130, 161, 0.25);
          }
          .btn-primary {
            background-color: #2582A1;
            border-color: #2582A1;
          }
          .btn-primary:hover,
          .btn-primary:focus {
            background-color: #1f6b86;
            border-color: #1f6b86;
          }
          .btn-outline-primary {
            color: #2582A1;
            border-color: #2582A1;
          }
          .btn-outline-primary:hover {
            background-color: #2582A1;
            border-color: #2582A1;
          }
          .link-primary {
            color: #2582A1 !important;
            text-decoration: none;
          }
          .link-primary:hover {
            text-decoration: underline !important;
          }
        `}
      </style>

      {/* Custom Alert Component */}
      <CustomAlert
        show={alertConfig.show}
        onClose={hideAlert}
        variant={alertConfig.variant}
        title={alertConfig.title}
        message={alertConfig.message}
        icon={alertConfig.icon}
        autoClose={alertConfig.autoClose}
        autoCloseDelay={alertConfig.autoCloseDelay}
        position={alertConfig.position}
        animation="bounce"
        overlay={true}
      />

      <FromPageIndicator />

      <div className="text-dark py-4 mb-4">
        <Container>
          <h1 className="text-center mb-2" style={{ color: "#2582A1" }}>
            Reset Your Password
          </h1>
          <p className="text-center mb-0">
            Enter your email address to receive your account details
          </p>
        </Container>
      </div>

      <Container className="pb-5">
        <Row className="justify-content-center">
          <Col lg={5} md={8}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                <div className="d-flex align-items-center mb-3">
                  <Link
                    to="/login-page"
                    className="btn btn-outline-primary btn-sm me-3"
                    style={{ minWidth: "auto", padding: "0.375rem 0.75rem" }}
                  >
                    <ArrowLeft size={16} className="me-1" />
                    Back to Login
                  </Link>
                </div>
                <h3 className="mb-0 text-center">Forgot Password</h3>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                <div className="px-lg-4">
                  <Alert variant="info" className="mb-4">
                    <strong>How it works:</strong>
                    <ul className="mb-0 mt-2">
                      <li>Enter your registered email address</li>
                      <li>We'll send your current password to your email</li>
                      <li>Use the password to login to your account</li>
                    </ul>
                  </Alert>

                  <Form onSubmit={handleSubmit}>
                    <Form.Group controlId="resetEmail" className="mb-4">
                      <Form.Label className="fw-semibold">
                        Email Address <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-light">
                          <EnvelopeFill />
                        </span>
                        <Form.Control
                          type="email"
                          placeholder="Enter your registered email address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onKeyPress={handleKeyPress}
                          disabled={loading}
                          required
                          className="py-2"
                        />
                      </div>
                    </Form.Group>

                    <div className="d-grid">
                      <Button
                        type="submit"
                        size="lg"
                        className="py-2 btn-primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            Sending Email...
                          </>
                        ) : (
                          "Send My Password"
                        )}
                      </Button>
                    </div>
                  </Form>

                  <div className="text-center mt-4 pt-3 border-top">
                    <p className="text-muted mb-2">
                      Remember your password?{" "}
                      <Link to="/login-page" className="link-primary">
                        Back to Login
                      </Link>
                    </p>
                    <p className="text-muted mb-0">
                      Don't have an account?{" "}
                      <Link to="/registration" className="link-primary">
                        Register Now
                      </Link>
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="justify-content-center mt-4">
          <Col lg={5} md={8}>
            <Card className="border-0 shadow-sm bg-light">
              <Card.Body className="p-4">
                <h5 className="mb-3">Need Help?</h5>
                <p className="mb-2">
                  If you're having trouble accessing your account, please
                  contact our support team:
                </p>
                <p className="mb-1">
                  <strong>Email:</strong> tnpyampicon2025@gmail.com
                </p>
                <p className="mb-0">
                  <strong>Phone:</strong> +91 9994432135
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ForgetPassword;
